import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Target, Server, BarChart3, Users, Cog, GraduationCap,
  ChevronRight, ChevronLeft, CheckCircle, AlertCircle, AlertTriangle,
  Loader2, Building2, Mail, User, Briefcase, DollarSign,
  TrendingUp, Zap, Shield, Clock, Download, ArrowRight,
  Lightbulb, Award, Rocket, PieChart, Activity, Home,
  MessageSquare, ChevronDown, ChevronUp, ExternalLink, Globe,
  ArrowUpRight, Calendar, Flag, Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ReportSecurityNote } from '../components/ReportSecurityNote';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { submitMaturity } from '../utils/maturityJobs';
import { generateMaturityPptx } from '../utils/generateMaturityPptx';
import AGIInsightsTab from '../components/agi/AGIInsightsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Target: Target,
  Server: Server,
  BarChart3: BarChart3,
  Users: Users,
  Cog: Cog,
  GraduationCap: GraduationCap
};

// Company size options
const companySizes = [
  { value: "1-50", label: "1-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-1000", label: "201-1,000 employees" },
  { value: "1001-5000", label: "1,001-5,000 employees" },
  { value: "5000+", label: "5,000+ employees" }
];

// Revenue options
const revenueRanges = [
  { value: "$0-1M", label: "Under $1 Million" },
  { value: "$1M-10M", label: "$1M - $10 Million" },
  { value: "$10M-100M", label: "$10M - $100 Million" },
  { value: "$100M-1B", label: "$100M - $1 Billion" },
  { value: "$1B+", label: "Over $1 Billion" }
];

// Industries - all 33 supported industries
const industries = [
  'Aerospace & Defense', 'Agriculture', 'Automotive', 'Biotechnology', 'Chemicals',
  'Consumer Packaged Goods', 'Cosmetics & Personal Care', 'Education', 'Energy',
  'EPC (Engineering, Procurement & Construction)',
  'Financial Services', 'Food Processing', 'Healthcare', 'Insurance', 'Manufacturing',
  'Media & Entertainment', 'Medical Devices', 'Metals & Mining', 'Nonprofit', 'Oil & Gas',
  'Pharmaceuticals', 'Private Equity', 'Professional Services', 'Pulp & Paper',
  'Real Estate & Construction', 'Restaurant', 'Retail', 'Social Sector & NGOs', 'Software',
  'Technology', 'Telecommunications', 'Travel & Logistics', 'Utilities', 'Waste Management', 'Wine, Beer & Spirits'
];

const DigitalMaturityPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentIdFromUrl = searchParams.get('id');
  
  const [step, setStep] = useState(0); // 0: intro, 1: profile, 2+: dimensions, final: results
  const [loading, setLoading] = useState(false);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [questionsData, setQuestionsData] = useState(null);
  const [responses, setResponses] = useState({});
  const [results, setResults] = useState(null);
  const [fullResults, setFullResults] = useState(null);
  const [error, setError] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [showContextInput, setShowContextInput] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [assessmentId, setAssessmentId] = useState(assessmentIdFromUrl || null);
  
  // Profile form state
  const [profile, setProfile] = useState({
    company_name: '',
    company_url: '',
    industry: '',
    company_size: '',
    annual_revenue: '',
    respondent_name: '',
    respondent_role: '',
    respondent_email: ''
  });
  
  // URL scraping state
  const [scrapingUrl, setScrapingUrl] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);

  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
  }, []);
  
  // Load assessment from URL if id parameter is present
  useEffect(() => {
    if (assessmentIdFromUrl) {
      loadAssessmentFromUrl(assessmentIdFromUrl);
    }
  }, [assessmentIdFromUrl]);
  
  const loadAssessmentFromUrl = async (id) => {
    setLoadingAssessment(true);
    try {
      const res = await axios.get(`${API}/maturity/assessment/${id}`);
      if (res.data) {
        setAssessmentId(id);
        const assessment = res.data;
        // Check both code_verified and is_sample_preview flags
        const isSamplePreview = assessment.is_sample_preview === true;
        const isVerified = assessment.code_verified === true || !isSamplePreview;
        
        // Store email in profile for verification
        if (assessment.respondent_email) {
          setProfile(prev => ({ ...prev, respondent_email: assessment.respondent_email }));
        }
        
        setResults({
          success: true,
          assessment_id: id,
          id: id,
          respondent_email: assessment.respondent_email,
          requires_secret_code: !isVerified,
          is_sample_preview: isSamplePreview,
          company_name: assessment.company_name,
          industry: assessment.industry,
          overall_score: assessment.overall_score,
          maturity_level: assessment.maturity_level,
          industry_benchmark: assessment.industry_benchmark,
          dimension_scores: assessment.dimension_scores || [],
          total_dimensions_count: assessment.total_dimensions_count || assessment.dimension_scores?.length || 7,
          ai_analysis: isSamplePreview ? null : assessment.ai_analysis  // Hide AI analysis for sample preview
        });
        
        // Only set full results if verified and not a sample preview
        if (isVerified && !isSamplePreview) {
          setFullResults(assessment);
        }
        
        setStep(999);
      }
    } catch (err) {
      console.error('Error loading assessment:', err);
      setError('Assessment not found or has expired. Please start a new assessment.');
    } finally {
      setLoadingAssessment(false);
    }
  };
  
  const verifySecretCode = async () => {
    if (!secretCode || secretCode.length !== 6) {
      setCodeError('Please enter a valid 6-digit code');
      return;
    }
    
    setVerifyingCode(true);
    setCodeError('');
    
    try {
      // Use profile email, fall back to results email
      const email = profile.respondent_email || results.respondent_email || '';
      
      const res = await axios.post(`${API}/maturity/verify-code`, {
        assessment_id: assessmentId || results.assessment_id || results.id,
        secret_code: secretCode,
        email: email
      });
      
      if (res.data.verified) {
        setFullResults(res.data.full_results);
      }
    } catch (err) {
      // Handle different error response formats
      const detail = err.response?.data?.detail;
      let errorMessage = 'Invalid secret code';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message).join(', ') || errorMessage;
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || errorMessage;
      }
      
      setCodeError(errorMessage);
    } finally {
      setVerifyingCode(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/maturity/questions`);
      setQuestionsData(res.data);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to load assessment questions');
    }
  };

  const dimensions = questionsData ? Object.keys(questionsData.dimensions) : [];
  const totalSteps = dimensions.length + 2; // intro + profile + dimensions + results

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleResponseChange = (questionId, value, dimensionId) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { question_id: questionId, selected_value: value, dimension: dimensionId }
    }));
  };

  const validateProfile = () => {
    if (!profile.company_name) return 'Please enter company name';
    if (!profile.industry) return 'Please select industry';
    if (!profile.company_size) return 'Please select company size';
    if (!profile.annual_revenue) return 'Please select annual revenue';
    if (!profile.respondent_name) return 'Please enter your name';
    if (!profile.respondent_role) return 'Please enter your role';
    if (!profile.respondent_email) return 'Please enter your email';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.respondent_email)) return 'Please enter a valid email';
    return null;
  };

  // Scrape company URL for digital transformation reports
  const scrapeCompanyUrl = async () => {
    if (!profile.company_url) return;
    
    setScrapingUrl(true);
    try {
      const formData = new FormData();
      formData.append('url', profile.company_url);
      formData.append('report_type', 'digital_transformation');
      
      const res = await axios.post(`${API}/scrape-company-report`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      setScrapedData(res.data);
    } catch (err) {
      console.error('Error scraping URL:', err);
      setScrapedData({ success: false, error: 'Failed to analyze URL' });
    } finally {
      setScrapingUrl(false);
    }
  };

  const validateDimension = (dimensionId) => {
    if (!questionsData) return null;
    const questions = questionsData.dimensions[dimensionId].questions;
    for (const q of questions) {
      if (!responses[q.id]) {
        return `Please answer all questions in ${questionsData.dimensions[dimensionId].name}`;
      }
    }
    return null;
  };

  const handleNext = () => {
    if (step === 1) {
      const validationError = validateProfile();
      if (validationError) {
        setError(validationError);
        return;
      }
    } else if (step > 1 && step <= dimensions.length + 1) {
      const currentDimension = dimensions[step - 2];
      const validationError = validateDimension(currentDimension);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setError('');
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    // Validate last dimension
    const lastDimension = dimensions[dimensions.length - 1];
    const validationError = validateDimension(lastDimension);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('company_name', profile.company_name);
      formData.append('industry', profile.industry);
      formData.append('company_size', profile.company_size);
      formData.append('annual_revenue', profile.annual_revenue);
      formData.append('respondent_name', profile.respondent_name);
      formData.append('respondent_role', profile.respondent_role);
      formData.append('respondent_email', profile.respondent_email);
      formData.append('responses', JSON.stringify(Object.values(responses)));
      if (additionalContext.trim()) {
        formData.append('additional_context', additionalContext.trim());
      }
      if (profile.company_url) {
        formData.append('company_url', profile.company_url);
      }
      if (scrapedData?.success && scrapedData?.extracted_info) {
        formData.append('scraped_data', JSON.stringify(scrapedData.extracted_info));
      }

      const res = await submitMaturity(`${API}/maturity/assess`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });

      setResults(res.data);
      setStep(totalSteps); // Go to results
    } catch (err) {
      console.error('Assessment error:', err);
      setError(err.response?.data?.detail || 'Failed to process assessment');
    } finally {
      setLoading(false);
    }
  };

  const getProgressPercentage = () => {
    if (step === 0) return 0;
    return Math.round((step / (totalSteps - 1)) * 100);
  };

  // Render intro screen
  const renderIntro = () => (
    <div className="max-w-4xl mx-auto text-center" data-testid="maturity-intro">
      <div className="mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <Activity className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
          Digital Maturity Assessment
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
          Discover your organization&apos;s digital transformation readiness with our AI-powered assessment, 
          benchmarked against industry standards and best practices.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
            <PieChart className="w-6 h-6 text-cyan-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">6 Dimensions</h3>
          <p className="text-sm text-gray-600">Comprehensive assessment across strategy, technology, data, customer, operations, and workforce</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
            <Lightbulb className="w-6 h-6 text-emerald-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">AI-Powered Insights</h3>
          <p className="text-sm text-gray-600">Get personalized recommendations and strategic roadmap from our AI consultant</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
            <Award className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Executive Report</h3>
          <p className="text-sm text-gray-600">Professional CXO-ready report with benchmarks, quick wins, and investment priorities</p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white mb-8">
        <h3 className="text-xl font-semibold mb-4">What You&apos;ll Discover</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Maturity Score</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Industry Benchmark</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Strategic Roadmap</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Investment Priorities</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Quick Wins</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Gap Analysis</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Transformation Themes</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>PPTX Download</span>
          </div>
        </div>
      </div>

      <Button 
        onClick={() => setStep(1)} 
        className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white px-8 py-6 text-lg rounded-xl"
        data-testid="start-assessment-btn"
      >
        Start Assessment
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
      
      <p className="text-sm text-gray-500 mt-4">Takes approximately 10-15 minutes to complete</p>
    </div>
  );

  // Render profile form
  const renderProfile = () => (
    <div className="max-w-2xl mx-auto" data-testid="maturity-profile">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Organization Profile</h2>
        <p className="text-gray-600">Tell us about your organization to personalize the assessment</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={profile.company_name}
              onChange={(e) => handleProfileChange('company_name', e.target.value)}
              placeholder="Enter company name"
              className="pl-11 h-12"
              data-testid="input-company-name"
            />
          </div>
        </div>

        {/* Company URL for scraping */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Website / Digital Strategy Report URL
            <span className="text-xs text-gray-500 ml-1">(Optional - we'll analyze it)</span>
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={profile.company_url}
                onChange={(e) => handleProfileChange('company_url', e.target.value)}
                placeholder="https://company.com/digital-strategy"
                className="pl-11 h-12"
                data-testid="input-company-url"
              />
            </div>
            {profile.company_url && (
              <Button
                type="button"
                variant="outline"
                onClick={scrapeCompanyUrl}
                disabled={scrapingUrl}
                className="h-12 px-4"
              >
                {scrapingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              </Button>
            )}
          </div>
          {scrapedData?.success && (
            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Report analyzed - insights will inform your assessment
            </p>
          )}
          {scrapedData?.success === false && (
            <p className="text-xs text-gray-500 mt-1">Could not analyze URL. Assessment will proceed normally.</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Industry *</label>
          <Select value={profile.industry} onValueChange={(v) => handleProfileChange('industry', v)}>
            <SelectTrigger className="h-12" data-testid="select-industry">
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {industries.map(ind => (
                <SelectItem key={ind} value={ind}>{ind}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Size *</label>
            <Select value={profile.company_size} onValueChange={(v) => handleProfileChange('company_size', v)}>
              <SelectTrigger className="h-12" data-testid="select-company-size">
                <SelectValue placeholder="Select size" />
              </SelectTrigger>
              <SelectContent>
                {companySizes.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Annual Revenue *</label>
            <Select value={profile.annual_revenue} onValueChange={(v) => handleProfileChange('annual_revenue', v)}>
              <SelectTrigger className="h-12" data-testid="select-revenue">
                <SelectValue placeholder="Select revenue" />
              </SelectTrigger>
              <SelectContent>
                {revenueRanges.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <hr className="my-6 border-gray-200" />
        <p className="text-sm text-gray-500 mb-4">Your Information (for the report)</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={profile.respondent_name}
                onChange={(e) => handleProfileChange('respondent_name', e.target.value)}
                placeholder="John Doe"
                className="pl-11 h-12"
                data-testid="input-name"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Role *</label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={profile.respondent_role}
                onChange={(e) => handleProfileChange('respondent_role', e.target.value)}
                placeholder="Chief Digital Officer"
                className="pl-11 h-12"
                data-testid="input-role"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="email"
              value={profile.respondent_email}
              onChange={(e) => handleProfileChange('respondent_email', e.target.value)}
              placeholder="john@company.com"
              className="pl-11 h-12"
              data-testid="input-email"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render dimension questions
  const renderDimension = (dimensionId) => {
    if (!questionsData) return null;
    const dimension = questionsData.dimensions[dimensionId];
    const IconComponent = iconMap[dimension.icon] || Target;

    return (
      <div className="max-w-3xl mx-auto" data-testid={`dimension-${dimensionId}`}>
        <div className="text-center mb-8">
          <div className={`w-16 h-16 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-4`}>
            <IconComponent className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{dimension.name}</h2>
          <p className="text-gray-600">{dimension.description}</p>
        </div>

        <div className="space-y-6">
          {dimension.questions.map((question, qIndex) => (
            <div key={question.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-semibold text-gray-600">
                  {qIndex + 1}
                </span>
                <h3 className="text-lg font-medium text-gray-900">{question.question}</h3>
              </div>

              <div className="space-y-3 ml-11">
                {question.options.map((option) => {
                  const isSelected = responses[question.id]?.selected_value === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleResponseChange(question.id, option.value, dimensionId)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid={`option-${question.id}-${option.value}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-500 mt-1">{option.description}</div>
                        </div>
                        <div className="ml-auto flex-shrink-0">
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${
                            option.value <= 2 ? 'bg-red-100 text-red-700' :
                            option.value === 3 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            Level {option.value}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render loading state during submission
  const renderLoading = () => (
    <div className="max-w-2xl mx-auto text-center py-16">
      <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyzing Your Digital Maturity</h2>
      <p className="text-gray-600 mb-8">Our AI is generating your personalized assessment report with strategic recommendations...</p>
      <div className="space-y-2 text-sm text-gray-500">
        <p>✓ Calculating dimension scores</p>
        <p>✓ Benchmarking against industry standards</p>
        <p>✓ Identifying strengths and gaps</p>
        <p className="animate-pulse">⟳ Generating strategic recommendations...</p>
      </div>
    </div>
  );

  // Main render
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
        {renderLoading()}
      </div>
    );
  }
  
  // Loading state when loading assessment from URL
  if (loadingAssessment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Loading Your Assessment...</h2>
          <p className="text-gray-500 mt-2">Please wait while we retrieve your results</p>
        </div>
      </div>
    );
  }

  if (results) {
    return (
      <MaturityResults 
        results={fullResults || results} 
        onStartNew={() => { setResults(null); setFullResults(null); setStep(0); setResponses({}); }}
        requiresSecretCode={!fullResults && results.requires_secret_code !== false}
        secretCode={secretCode}
        setSecretCode={setSecretCode}
        verifySecretCode={verifySecretCode}
        verifyingCode={verifyingCode}
        codeError={codeError}
      />
    );
  }

  const currentDimensionIndex = step - 2;
  const isLastDimension = currentDimensionIndex === dimensions.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 sm:py-12 px-4">
      {/* Home Button */}
      <div className="max-w-4xl mx-auto mb-6">
        <Link to="/">
          <Button variant="outline" className="gap-2 text-gray-600 hover:text-emerald-600 hover:border-emerald-600">
            <Home className="w-4 h-4" />
            Home
          </Button>
        </Link>
      </div>

      {/* Progress bar */}
      {step > 0 && (
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Progress</span>
            <span className="text-sm font-medium text-emerald-600">{getProgressPercentage()}%</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>
      )}

      {/* Step content */}
      {step === 0 && renderIntro()}
      {step === 1 && renderProfile()}
      {step > 1 && step <= dimensions.length + 1 && dimensions[currentDimensionIndex] && 
        renderDimension(dimensions[currentDimensionIndex])}

      {/* Additional Context Input - shown on last dimension */}
      {isLastDimension && (
        <div className="max-w-3xl mx-auto mt-8">
          <div className="bg-gradient-to-r from-cyan-50 to-emerald-50 border border-cyan-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowContextInput(!showContextInput)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/50 transition-colors"
              data-testid="toggle-context-input"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Additional Context (Optional)</h4>
                  <p className="text-sm text-gray-600">Share specific challenges, goals, or context for personalized insights</p>
                </div>
              </div>
              {showContextInput ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {showContextInput && (
              <div className="px-6 pb-6">
                <textarea
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Examples:&#10;• We're planning a major ERP migration next quarter&#10;• Budget constraints are our biggest challenge&#10;• We want to focus on customer-facing digital channels&#10;• Our legacy systems are 15+ years old"
                  className="w-full h-32 p-4 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-gray-700 placeholder:text-gray-400"
                  maxLength={1000}
                  data-testid="additional-context-input"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    This helps our AI provide more relevant recommendations
                  </p>
                  <span className="text-xs text-gray-400">{additionalContext.length}/1000</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="max-w-2xl mx-auto mt-6">
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      {step > 0 && (
        <div className="max-w-3xl mx-auto mt-8 flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            className="px-6 py-3"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {isLastDimension ? (
            <Button
              onClick={handleSubmit}
              className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 px-8 py-3"
              data-testid="btn-submit"
            >
              Generate Report
              <Rocket className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-emerald-600 hover:bg-emerald-700 px-6 py-3"
              data-testid="btn-next"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Results Component
const MaturityResults = ({ 
  results, 
  onStartNew,
  requiresSecretCode = false,
  secretCode = '',
  setSecretCode,
  verifySecretCode,
  verifyingCode = false,
  codeError = ''
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Get color class based on score
  const getScoreColor = (score) => {
    if (score < 2) return 'text-red-600';
    if (score < 3) return 'text-orange-500';
    if (score < 4) return 'text-yellow-600';
    return 'text-emerald-600';
  };

  const getScoreBg = (score) => {
    if (score < 2) return 'bg-red-100';
    if (score < 3) return 'bg-orange-100';
    if (score < 4) return 'bg-yellow-100';
    return 'bg-emerald-100';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'dimensions', label: 'Dimensions', icon: BarChart3 },
    { id: 'kpis', label: 'Industry KPIs', icon: Activity },
    { id: 'roadmap', label: 'Roadmap', icon: Rocket },
    { id: 'recommendations', label: 'Recommendations', icon: Lightbulb },
    { id: 'investment', label: 'Investment', icon: DollarSign },
    { id: 'agi', label: 'AGI Insights', icon: Brain }
  ];

  // If secret code is required, show the verification UI
  if (requiresSecretCode) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with preview info */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white mb-8">
            <div className="flex justify-between items-start">
              <div>
                <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <AlertCircle className="w-4 h-4" />
                  Sample Preview
                </div>
                <h1 className="text-3xl font-bold mb-2">{results.company_name}</h1>
                <p className="text-gray-400">{results.industry} • Digital Maturity Assessment</p>
              </div>
              <div className="text-right">
                <div className="text-5xl font-bold" style={{ color: results.maturity_color || '#10B981' }}>
                  {results.overall_score?.toFixed(1) || 'N/A'}
                </div>
                <div className="text-lg font-semibold" style={{ color: results.maturity_color || '#10B981' }}>
                  {results.overall_maturity_level || results.maturity_level || 'Assessment Complete'}
                </div>
                <div className="text-sm text-gray-400">out of 5.0</div>
              </div>
            </div>
          </div>

          {/* Sample Dimension Scores */}
          {results.dimension_scores?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Dimension Scores (Sample)</h2>
              <div className="space-y-4">
                {results.dimension_scores?.slice(0, 3).map((dim) => {
                  const isUnderperforming = dim.score < (results.industry_benchmark || 3.0);
                  return (
                    <div key={dim.dimension_id} className={`p-4 rounded-xl border-2 ${isUnderperforming ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                      <div className="flex justify-between items-center">
                        <span className={`font-semibold ${isUnderperforming ? 'text-red-800' : 'text-gray-900'}`}>
                          {dim.dimension_name}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className={`text-xl font-bold ${isUnderperforming ? 'text-red-600' : 'text-emerald-600'}`}>
                            {dim.score?.toFixed(1)}/5.0
                          </span>
                          {isUnderperforming && (
                            <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-600 text-white">
                              BELOW BENCHMARK
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 p-3 bg-gray-50 rounded border border-dashed border-gray-300">
                <p className="text-sm text-gray-500 italic">
                  {(results.total_dimensions_count || results.dimension_scores?.length || 6) - 3} more dimensions available with full access
                </p>
              </div>
            </div>
          )}

          {/* Industry Benchmark Preview */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Industry Benchmark</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-cyan-50 to-emerald-50 rounded-xl p-4 text-center border border-cyan-200">
                <div className="text-sm text-gray-500 mb-1">Your Score</div>
                <div className="text-3xl font-bold" style={{ color: results.maturity_color || '#10B981' }}>
                  {results.overall_score?.toFixed(1) || 'N/A'}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center border border-gray-200">
                <div className="text-sm text-gray-500 mb-1">Industry Average</div>
                <div className="text-3xl font-bold text-gray-600">
                  {results.industry_benchmark?.toFixed(1) || '3.0'}
                </div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-200">
                <div className="text-sm text-gray-500 mb-1">Top Performers</div>
                <div className="text-3xl font-bold text-emerald-600">4.5</div>
              </div>
            </div>
          </div>

          {/* Unlock Section */}
          <div className="bg-gradient-to-br from-cyan-600 to-emerald-600 rounded-2xl p-8 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Unlock Full Assessment Report</h2>
            <p className="text-cyan-100 mb-6 max-w-md mx-auto">
              Enter your 6-digit secret code to access the complete report with detailed analysis, 
              roadmap, recommendations, and downloadable presentation.
            </p>
            
            <div className="max-w-sm mx-auto">
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  maxLength={6}
                  value={secretCode}
                  onChange={(e) => setSecretCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 6-digit code"
                  className="flex-1 text-center text-2xl tracking-[0.3em] font-mono border-2 border-white/30 rounded-lg px-4 py-3 bg-white/10 text-white placeholder-white/50 focus:border-white focus:ring-white"
                />
                <Button
                  onClick={verifySecretCode}
                  disabled={verifyingCode || secretCode.length !== 6}
                  className="bg-white text-cyan-600 hover:bg-white/90 px-6 font-bold"
                >
                  {verifyingCode ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Unlock'}
                </Button>
              </div>
              {codeError && (
                <p className="text-red-200 text-sm mt-2 text-center">{codeError}</p>
              )}
            </div>

            <div className="mt-6 p-4 bg-white/10 rounded-lg text-left max-w-md mx-auto">
              <h3 className="font-semibold mb-2">What you&apos;ll unlock:</h3>
              <ul className="text-sm text-cyan-100 space-y-1">
                <li>• Complete 6-dimension analysis with recommendations</li>
                <li>• Strategic implementation roadmap</li>
                <li>• Industry benchmarks and competitive position</li>
                <li>• Investment priorities and ROI projections</li>
                <li>• Level Up: Journey to next maturity level</li>
                <li>• Downloadable PowerPoint presentation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4" data-testid="maturity-results">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">Digital Maturity Assessment</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">{results.company_name}</h1>
              <p className="text-gray-400">{results.industry} • {results.company_size} employees • {results.annual_revenue}</p>
            </div>
            <div className="text-center lg:text-right">
              <div className="text-6xl font-bold mb-2" style={{ color: results.maturity_color }}>
                {results.overall_score.toFixed(1)}
              </div>
              <div className="text-lg font-semibold" style={{ color: results.maturity_color }}>
                {results.overall_maturity_level}
              </div>
              <div className="text-sm text-gray-400 mt-1">out of 5.0</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-6xl mx-auto">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Executive Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Executive Summary
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{results.executive_summary}</p>
            </div>

            {/* Key Findings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                Key Findings
              </h2>
              <ul className="space-y-3">
                {results.key_findings?.map((finding, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{finding}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Benchmark Comparison */}
            {results.benchmark_comparison && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Industry Benchmark
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-sm text-gray-500 mb-1">Your Score</div>
                    <div className="text-3xl font-bold" style={{ color: results.maturity_color }}>
                      {results.overall_score.toFixed(1)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-sm text-gray-500 mb-1">Industry Average</div>
                    <div className="text-3xl font-bold text-gray-600">
                      {results.benchmark_comparison.industry_average || '3.0'}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-sm text-gray-500 mb-1">Top Performers</div>
                    <div className="text-3xl font-bold text-emerald-600">
                      {results.benchmark_comparison.top_performers || '4.5'}
                    </div>
                  </div>
                </div>
                {results.benchmark_comparison.competitive_position && (
                  <p className="mt-4 text-gray-700 bg-blue-50 p-4 rounded-lg">
                    <strong>Competitive Position:</strong> {results.benchmark_comparison.competitive_position}
                  </p>
                )}
              </div>
            )}

            {/* Quick Wins */}
            {results.quick_wins?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Quick Wins
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.quick_wins.map((win, idx) => (
                    <div key={idx} className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-yellow-600" />
                        <span className="font-semibold text-gray-900">{win.initiative}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{win.description}</p>
                      <div className="flex gap-2">
                        <span className="text-xs bg-white px-2 py-1 rounded border">Effort: {win.effort}</span>
                        <span className="text-xs bg-white px-2 py-1 rounded border">Impact: {win.impact}</span>
                        <span className="text-xs bg-white px-2 py-1 rounded border">{win.timeline}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dimensions Tab */}
        {activeTab === 'dimensions' && (
          <div className="space-y-6">
            {/* Dimension Scores Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.dimension_scores?.map((dim) => {
                const IconComponent = iconMap[dim.icon] || Target;
                return (
                  <div key={dim.dimension_id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 ${getScoreBg(dim.score)} rounded-lg flex items-center justify-center`}>
                        <IconComponent className={`w-5 h-5 ${getScoreColor(dim.score)}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{dim.dimension_name}</h3>
                        <div className={`text-sm ${getScoreColor(dim.score)}`}>{dim.maturity_level}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Progress value={dim.percentage} className="h-2" />
                      </div>
                      <span className={`text-xl font-bold ${getScoreColor(dim.score)}`}>
                        {dim.score.toFixed(1)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Detailed Dimension Analysis */}
            {results.dimension_scores?.map((dim) => (
              <div key={dim.dimension_id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{dim.dimension_name} Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Strengths
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      {dim.strengths?.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Gaps
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      {dim.gaps?.map((g, i) => <li key={i}>• {g}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" /> Recommendations
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      {dim.recommendations?.map((r, i) => <li key={i}>• {r}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Industry KPIs Tab */}
        {activeTab === 'kpis' && (
          <div className="space-y-6">
            {/* KPI Improvement Targets */}
            {results.kpi_improvement_targets?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  KPI Improvement Targets
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  Based on industry benchmarks for {results.industry}, here are your key performance indicators with improvement targets.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 font-semibold text-gray-700">KPI</th>
                        <th className="text-left py-3 px-2 font-semibold text-gray-700">Process Area</th>
                        <th className="text-center py-3 px-2 font-semibold text-gray-700">Maturity</th>
                        <th className="text-center py-3 px-2 font-semibold text-gray-700">Current</th>
                        <th className="text-center py-3 px-2 font-semibold text-gray-700">Industry Avg</th>
                        <th className="text-center py-3 px-2 font-semibold text-gray-700">Top Performer</th>
                        <th className="text-center py-3 px-2 font-semibold text-gray-700">6-Mo Target</th>
                        <th className="text-center py-3 px-2 font-semibold text-gray-700">12-Mo Target</th>
                        <th className="text-center py-3 px-2 font-semibold text-gray-700">Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.kpi_improvement_targets.map((kpi, idx) => (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <div className="font-medium text-gray-900">{kpi.kpi_name}</div>
                          </td>
                          <td className="py-3 px-2 text-gray-600">{kpi.process_area}</td>
                          <td className="py-3 px-2 text-center">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                              kpi.maturity_score <= 1 ? 'bg-red-100 text-red-700' :
                              kpi.maturity_score <= 2 ? 'bg-orange-100 text-orange-700' :
                              kpi.maturity_score <= 3 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {kpi.maturity_score}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center text-gray-600">{kpi.current_estimate}</td>
                          <td className="py-3 px-2 text-center text-blue-600">{kpi.industry_benchmark}</td>
                          <td className="py-3 px-2 text-center text-emerald-600 font-medium">{kpi.top_performer_benchmark}</td>
                          <td className="py-3 px-2 text-center text-purple-600">{kpi.target_6_month}</td>
                          <td className="py-3 px-2 text-center text-cyan-600 font-medium">{kpi.target_12_month}</td>
                          <td className="py-3 px-2 text-center">
                            <span className={`text-xs px-2 py-1 rounded ${
                              kpi.priority === 'High' ? 'bg-red-100 text-red-700' :
                              kpi.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {kpi.priority}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Maturity Scale Legend */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Digital Maturity Scale (0-5)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { score: 0, label: 'Manual', desc: 'No digitization', color: 'bg-red-100 text-red-700' },
                  { score: 1, label: 'Digitized', desc: 'Basic digital', color: 'bg-orange-100 text-orange-700' },
                  { score: 2, label: 'Automated', desc: 'Rules-based', color: 'bg-yellow-100 text-yellow-700' },
                  { score: 3, label: 'Optimized', desc: 'Data-driven', color: 'bg-lime-100 text-lime-700' },
                  { score: 4, label: 'Predictive', desc: 'AI-assisted', color: 'bg-emerald-100 text-emerald-700' },
                  { score: 5, label: 'Autonomous', desc: 'Self-learning', color: 'bg-cyan-100 text-cyan-700' }
                ].map(level => (
                  <div key={level.score} className={`${level.color} rounded-lg p-3 text-center`}>
                    <div className="text-2xl font-bold">{level.score}</div>
                    <div className="text-sm font-semibold">{level.label}</div>
                    <div className="text-xs mt-1 opacity-80">{level.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Industry KPI Reference */}
            {results.industry_kpis?.process_areas && Object.keys(results.industry_kpis.process_areas).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  {results.industry} - Industry KPI Reference
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(results.industry_kpis.process_areas).map(([areaName, areaData]) => (
                    <div key={areaName} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                        {areaName}
                      </h4>
                      <ul className="space-y-2 text-sm">
                        {areaData.kpis?.slice(0, 5).map((kpi, idx) => (
                          <li key={idx} className="flex justify-between items-start gap-2">
                            <span className="text-gray-700">{kpi.name}</span>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded whitespace-nowrap">
                              Top: {kpi.benchmark_top} {kpi.unit}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Roadmap Tab */}
        {activeTab === 'roadmap' && (
          <div className="space-y-6">
            {results.implementation_roadmap?.map((phase, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-xl flex items-center justify-center text-white font-bold">
                    {idx + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{phase.phase}</h3>
                    <p className="text-sm text-gray-500">{phase.duration}</p>
                  </div>
                  {phase.investment_range && (
                    <span className="ml-auto bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
                      {phase.investment_range}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Focus Areas</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {phase.focus_areas?.map((f, i) => <li key={i}>• {f}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Key Initiatives</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {phase.key_initiatives?.map((k, i) => <li key={i}>• {k}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Success Metrics</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {phase.success_metrics?.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            ))}

            {/* Transformation Themes */}
            {results.transformation_themes?.length > 0 && (
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-4">Transformation Themes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.transformation_themes.map((theme, idx) => (
                    <div key={idx} className="bg-white/10 rounded-lg p-4">
                      <h4 className="font-semibold text-cyan-400 mb-2">{theme.theme}</h4>
                      <p className="text-sm text-gray-300 mb-3">{theme.description}</p>
                      <div className="text-xs text-gray-400">
                        <strong>Initiatives:</strong> {theme.initiatives?.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            {results.strategic_recommendations?.map((rec, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                    rec.priority === 'Critical' ? 'bg-red-100 text-red-600' :
                    rec.priority === 'High' ? 'bg-orange-100 text-orange-600' :
                    'bg-yellow-100 text-yellow-600'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-bold text-gray-900">{rec.initiative}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${
                        rec.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                        rec.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {rec.priority}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4">{rec.description}</p>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="flex items-center gap-1 text-gray-500">
                        <Clock className="w-4 h-4" /> {rec.timeline}
                      </span>
                      <span className="flex items-center gap-1 text-gray-500">
                        <DollarSign className="w-4 h-4" /> {rec.investment}
                      </span>
                    </div>
                    {rec.expected_impact && (
                      <div className="mt-3 p-3 bg-emerald-50 rounded-lg text-sm text-emerald-800">
                        <strong>Expected Impact:</strong> {rec.expected_impact}
                      </div>
                    )}
                    {rec.kpis?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {rec.kpis.map((kpi, i) => (
                          <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {kpi}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Investment Tab */}
        {activeTab === 'investment' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {results.investment_priorities?.map((inv, idx) => (
                <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{inv.area}</h3>
                      <p className="text-sm text-gray-500">{inv.investment_required}</p>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Current State:</span>
                      <span className="text-gray-700">{inv.current_state}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Target State:</span>
                      <span className="text-emerald-600 font-medium">{inv.target_state}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">ROI Timeline:</span>
                      <span className="text-gray-700">{inv.roi_timeline}</span>
                    </div>
                  </div>
                  {inv.business_case && (
                    <p className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                      {inv.business_case}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Level Up Section - Journey to Next Digital Maturity Level */}
        {results?.ai_analysis?.level_progression && (
          <div className="bg-gradient-to-br from-cyan-50 via-teal-50 to-emerald-50 rounded-2xl shadow-lg p-8 mt-8 border-2 border-cyan-200" data-testid="level-up-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Level Up: Journey to Next Digital Maturity Level</h2>
                <p className="text-gray-600">What it takes to advance your digital transformation</p>
              </div>
            </div>

            {/* Current to Next Level Visualization */}
            <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div className="text-center flex-1">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                    <span className="text-3xl font-bold text-white">
                      {results.ai_analysis.level_progression.current_level?.level_number || Math.floor(results.overall_score)}
                    </span>
                  </div>
                  <div className="font-bold text-gray-900">Current Level</div>
                  <div className="text-sm text-gray-500">
                    {results.ai_analysis.level_progression.current_level?.level_name || results.maturity_level}
                  </div>
                </div>
                <div className="flex-1 px-4">
                  <div className="relative">
                    <div className="h-2 bg-gradient-to-r from-amber-400 via-cyan-400 to-teal-500 rounded-full" />
                    <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-center">
                      <div className="flex items-center gap-1 bg-cyan-100 px-3 py-1 rounded-full">
                        <Clock className="w-3 h-3 text-cyan-600" />
                        <span className="text-xs font-medium text-cyan-700">
                          {results.ai_analysis.level_progression.progression_requirements?.time_estimate || '6-12 months'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center flex-1">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-cyan-400 to-teal-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                    <span className="text-3xl font-bold text-white">
                      {results.ai_analysis.level_progression.next_level?.level_number || Math.min(Math.floor(results.overall_score) + 1, 5)}
                    </span>
                  </div>
                  <div className="font-bold text-gray-900">Target Level</div>
                  <div className="text-sm text-cyan-600 font-medium">
                    {results.ai_analysis.level_progression.next_level?.level_name || 'Next Level'}
                  </div>
                </div>
              </div>
            </div>

            {/* Resource Requirements Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-cyan-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="font-bold text-gray-900">Time</div>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {results.ai_analysis.level_progression.progression_requirements?.time_estimate || '6-12 months'}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-cyan-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="font-bold text-gray-900">Manpower</div>
                </div>
                <div className="text-lg font-bold text-purple-600">
                  {results.ai_analysis.level_progression.progression_requirements?.manpower_resources?.internal_fte || 'Not available'}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-cyan-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div className="font-bold text-gray-900">Investment</div>
                </div>
                <div className="text-lg font-bold text-cyan-600">
                  {results.ai_analysis.level_progression.progression_requirements?.investment_required?.total_range || 'Not available'}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-cyan-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="font-bold text-gray-900">Expected ROI</div>
                </div>
                <div className="text-lg font-bold text-amber-600">
                  {results.ai_analysis.level_progression.progression_requirements?.business_benefits?.quantified_value || 'Not available'}
                </div>
              </div>
            </div>

            {/* Milestones */}
            {results.ai_analysis.level_progression.milestones?.length > 0 && (
              <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Flag className="w-5 h-5 text-cyan-600" />
                  Key Milestones to Next Level
                </h3>
                <div className="relative">
                  <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-cyan-400 to-teal-500" />
                  <div className="space-y-6">
                    {results.ai_analysis.level_progression.milestones.map((milestone, idx) => (
                      <div key={idx} className="relative flex gap-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 z-10 bg-gradient-to-br from-cyan-500 to-teal-600 shadow-lg">
                          <span className="text-white font-bold">{milestone.milestone_number || idx + 1}</span>
                        </div>
                        <div className="flex-1 bg-gradient-to-r from-cyan-50 to-transparent rounded-lg p-4 border border-cyan-100">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-gray-900">{milestone.name}</h4>
                            <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {milestone.target_month}
                            </span>
                          </div>
                          {milestone.key_deliverables?.length > 0 && (
                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Deliverables:</strong>
                              <ul className="list-disc list-inside ml-2 mt-1">
                                {milestone.key_deliverables.slice(0, 3).map((d, i) => (
                                  <li key={i} className="text-gray-600">{d}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {milestone.success_criteria && (
                            <div className="text-xs text-cyan-600 font-medium">
                              ✓ Success: {milestone.success_criteria}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Business Benefits at Next Level */}
            <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
                Business Benefits at Next Level
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-medium text-gray-600">Efficiency Gains</span>
                  </div>
                  <div className="text-lg font-bold text-emerald-700">
                    {results.ai_analysis.level_progression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available'}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">Revenue Impact</span>
                  </div>
                  <div className="text-lg font-bold text-blue-700">
                    {results.ai_analysis.level_progression.progression_requirements?.business_benefits?.revenue_impact || 'Not available'}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-gray-600">Risk Reduction</span>
                  </div>
                  <div className="text-lg font-bold text-amber-700">
                    {results.ai_analysis.level_progression.progression_requirements?.business_benefits?.risk_reduction || 'Not available'}
                  </div>
                </div>
              </div>
            </div>

            {/* Key Challenges & Quick Wins */}
            <div className="grid md:grid-cols-2 gap-6">
              {results.ai_analysis.level_progression.key_challenges?.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-100">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Key Challenges
                  </h3>
                  <ul className="space-y-2">
                    {results.ai_analysis.level_progression.key_challenges.map((challenge, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-amber-700 text-xs font-bold">{idx + 1}</span>
                        </div>
                        {challenge}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {results.ai_analysis.level_progression.quick_wins_to_accelerate?.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-cyan-100">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-cyan-600" />
                    Quick Wins to Accelerate
                  </h3>
                  <ul className="space-y-2">
                    {results.ai_analysis.level_progression.quick_wins_to_accelerate.map((win, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                        {win}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AGI Insights Tab */}
        {activeTab === 'agi' && (
          <AGIInsightsTab
            assessmentType="Digital Maturity"
            assessmentId={results?.id || results?.assessment_id}
          />
        )}
      </div>

      <div className="max-w-6xl mx-auto"><ReportSecurityNote /></div>

      {/* Actions */}
      <div className="max-w-6xl mx-auto mt-8 flex flex-wrap gap-4 justify-center">
        <Button
          onClick={onStartNew}
          variant="outline"
          className="px-6"
          data-testid="btn-new-assessment"
        >
          Start New Assessment
        </Button>
        <Button
          onClick={() => generateMaturityPptx(results)}
          className="bg-emerald-600 hover:bg-emerald-700 px-6"
          data-testid="btn-download-pptx"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PPTX Report
        </Button>
      </div>
    </div>
  );
};

export default DigitalMaturityPage;
